import "./index.css";
import "./i18n";
import { Button, Toast } from "@heroui/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from "react-router";
import { AppShell } from "./components/AppShell";
import { LoadingBlock } from "./components/ui";
import { ApiError, setUnauthorizedHandler } from "./lib/api";
import { keys, queryClient, useMe } from "./lib/queries";
import { AccountPage } from "./pages/AccountPage";
import { AppDetailPage } from "./pages/AppDetailPage";
import { AppsPage } from "./pages/AppsPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { CredentialProfilePage } from "./pages/CredentialProfilePage";
import { CredentialProfilesPage } from "./pages/CredentialProfilesPage";
import { CredentialsPage } from "./pages/CredentialsPage";
import { CustomerDetailPage } from "./pages/CustomerDetailPage";
import { CustomersPage } from "./pages/CustomersPage";
import { DeliveryLogPage } from "./pages/DeliveryLogPage";
import { LoginPage } from "./pages/LoginPage";
import { MessageDetailPage } from "./pages/MessageDetailPage";
import { OverviewPage } from "./pages/OverviewPage";
import { ProvidersPage } from "./pages/ProvidersPage";
import { SendPage } from "./pages/SendPage";
import { SettingsPage } from "./pages/SettingsPage";

function RequireAuth() {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <LoadingBlock />;
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
    }
    throw me.error;
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function NotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center gap-3 py-24 text-center">
      <h1 className="text-xl font-semibold">{t("errors.notFound")}</h1>
      <p className="text-sm text-muted">{t("errors.notFoundHint")}</p>
      <Button variant="secondary" onPress={() => navigate("/")}>
        {t("nav.overview")}
      </Button>
    </div>
  );
}

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "/logs", element: <DeliveryLogPage /> },
      { path: "/send", element: <SendPage /> },
      { path: "/customers", element: <CustomersPage /> },
      { path: "/customers/:customerId", element: <CustomerDetailPage /> },
      { path: "/account", element: <AccountPage /> },
      { path: "/apps", element: <AppsPage /> },
      { path: "/apps/:appId", element: <AppDetailPage /> },
      { path: "/apps/:appId/messages/:messageId", element: <MessageDetailPage /> },
      { path: "/credentials", element: <CredentialsPage /> },
      { path: "/credential-profiles", element: <CredentialProfilesPage /> },
      { path: "/credential-profiles/:profileId", element: <CredentialProfilePage /> },
      { path: "/providers", element: <ProvidersPage /> },
      { path: "/audit", element: <AuditLogPage /> },
      { path: "/settings", element: <SettingsPage /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

// Session expired mid-use: reset the user query so RequireAuth redirects to sign-in.
setUnauthorizedHandler(() => {
  void queryClient.resetQueries({ queryKey: keys.me });
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toast.Provider placement="bottom end" />
    </QueryClientProvider>
  </StrictMode>,
);
